/* eslint-disable react/prop-types */
/**
 * 成员详情 · 支付宝签约的两套表格列定义
 * ------------------------------------------------------------------
 * 列定义外置（重构方案 §4.3）。这两套列里的按钮要回调**容器**里的 handler /
 * setter（标记失效、看人脸视频、改绑支付宝），所以导出的是**工厂函数**：
 * 由 AlipaySection 把 props 透传进来 —— 与 `columns/visibleActivityColumns(sort)`
 * 同一套路，避免把容器状态硬编码进列定义。
 *
 * @param handlers { handleMarkSignInvalid, setFaceVideoUrl, setFaceVideoVisible,
 *                   setIsAlipayModalVisible, setNewAlipayAccount }
 */
import React from 'react';
import { Button, Image, Space } from 'antd';
import { CaretRightFilled } from '@ant-design/icons';

import styles from '../index.less';

export const signAliColumns = ({
  handleMarkSignInvalid,
  setFaceVideoUrl,
  setFaceVideoVisible,
  setIsAlipayModalVisible,
  setNewAlipayAccount,
}) => [
  {
    title: '真实姓名',
    dataIndex: 'idName',
    hideInSearch: true,
  },
  {
    title: '支付宝账号',
    dataIndex: 'zfbId',
    hideInSearch: true,
    render: (_, record) => {
      const { zfbId } = record;
      if (zfbId) {
        return (
          <Space>
            {zfbId}
            {zfbId && (
              <a
                onClick={() => {
                  setNewAlipayAccount('');
                  setIsAlipayModalVisible(true);
                }}
              >
                修改
              </a>
            )}
          </Space>
        );
      } else {
        return <div>--</div>;
      }
    },
  },
  {
    title: '支付宝身份证号',
    dataIndex: 'idCardNo',
    hideInSearch: true,
    render: (_, record) => {
      const { idCardNo } = record;
      if (idCardNo) {
        return <div>{idCardNo}</div>;
      } else {
        return <div>--</div>;
      }
    },
  },
  {
    title: '零工卡签约状态',
    dataIndex: 'signStatus',
    hideInSearch: true,
    valueType: 'select',
    valueEnum: {
      0: { text: '初始化' },
      1: { text: '签约成功' },
      2: { text: '签约失败' },
    },
    // 新增空值处理渲染函数
    render: (_, record) => {
      const { signStatus } = record;
      const statusMap = {
        0: '初始化',
        1: '签约成功',
        2: '签约失败',
      };
      // 当 text 为 undefined/null/空时显示 --
      return signStatus === undefined || signStatus === null
        ? '--'
        : statusMap[signStatus];
    },
  },
  {
    title: '签约时间',
    dataIndex: 'signTime',
    hideInSearch: true,
    // 新增空值处理
    render: (_, record) => {
      const { signTime } = record;
      return signTime || '--';
    },
  },
  {
    title: '操作',
    dataIndex: 'option',
    valueType: 'option',
    hideInSearch: true,
    render: (_, record) => {
      // 无签约记录时与其他列一样展示 --，不渲染操作按钮
      if (record.signStatus === undefined || record.signStatus === null) {
        return <div>--</div>;
      }
      return (
        <Button type="link" onClick={() => handleMarkSignInvalid(record)}>
          标记失效
        </Button>
      );
    },
  },
];

export const contractDocumentsColumns = ({
  handleMarkSignInvalid,
  setFaceVideoUrl,
  setFaceVideoVisible,
  setIsAlipayModalVisible,
  setNewAlipayAccount,
}) => [
  {
    title: 'solution_code',
    dataIndex: 'solutionCode',
    key: 'solutionCode',
    hideInSearch: true,
    // 新增空值处理
    render: (_, record) => {
      const { solutionCode } = record;
      if (solutionCode && solutionCode != '-') {
        return <div>{solutionCode}</div>;
      } else {
        return <div>--</div>;
      }
    },
  },
  {
    title: '更新时间',
    dataIndex: 'gmtModified',
    hideInSearch: true,
    // 新增空值处理
    render: (_, record) => {
      const { gmtModified } = record;
      if (gmtModified && gmtModified != '-') {
        return <div>{gmtModified}</div>;
      } else {
        return <div>--</div>;
      }
    },
  },
  {
    title: '合同文件名称',
    dataIndex: 'contractDocuments',
    hideInSearch: true,
    render: (_, record) => {
      const { contractDocuments = [] } = record; // 防止数组不存在时报错
      // 若数组为空，显示无数据提示
      if (contractDocuments.length === 0) {
        return <div>--</div>;
      } else {
        return (
          <Space direction="vertical" className={styles.wFull}>
            {contractDocuments.map((doc, index) => (
              <Space key={index} align="center">
                {/* 展示文件名称 */}
                <span>{doc.fileName}</span>
                {/* 下载按钮 */}
                <Button
                  type="link"
                  onClick={() => {
                    // 创建临时a标签触发文件下载
                    const downloadLink = document.createElement('a');
                    downloadLink.href = doc.previewUrl; // 文件预览/下载地址
                    downloadLink.download = doc.fileName; // 下载后的文件名
                    downloadLink.click();
                  }}
                >
                  下载
                </Button>
              </Space>
            ))}
          </Space>
        );
      }
    },
  },
  {
    title: '人脸照片',
    dataIndex: 'faceImg',
    hideInSearch: true,
    render: (_, record) => {
      const { faceImg } = record;
      if (!faceImg) {
        return <div>--</div>;
      }
      // 点击图片查看大图
      return (
        <Image
          width={80}
          height={80}
          src={faceImg}
          className={styles.imgCover}
          placeholder
        />
      );
    },
  },
  {
    title: '人脸视频',
    dataIndex: 'faceVideo',
    hideInSearch: true,
    render: (_, record) => {
      const { faceVideo } = record;
      if (!faceVideo) {
        return <div>--</div>;
      }
      // 视频首帧作封面，点击弹窗播放
      return (
        <div
          title="点击播放"
          className={styles.videoThumb}
          onClick={() => {
            setFaceVideoUrl(faceVideo);
            setFaceVideoVisible(true);
          }}
        >
          <video
            src={`${faceVideo}${faceVideo.includes('#') ? '' : '#t=0.1'}`}
            preload="metadata"
            muted
            className={styles.videoFill}
          />
          <CaretRightFilled className={styles.videoPlayIcon} />
        </div>
      );
    },
  },
];
